import PauseIcon from '@app/_icons/PauseIcon/PauseIcon';
import PlayIcon from '@app/_icons/PlayIcon/PlayIcon';
import type { ChangeEvent, ComponentPropsWithoutRef, RefObject } from 'react';
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import { cn } from '@/lib/utils';

type VideoProps = ComponentPropsWithoutRef<'video'>;

function Progress({ videoRef }: { videoRef: RefObject<HTMLVideoElement> }) {
    const [progress, setProgress] = useState<string>('0');
    const onChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setProgress(event.currentTarget.value);
            event.preventDefault();
            if (videoRef?.current) {
                const value = parseInt(event.currentTarget.value, 10);
                const newTime = videoRef.current.duration * (value / 100);
                videoRef.current.currentTime = newTime;
            }
        },
        [videoRef],
    );

    useEffect(() => {
        const node = videoRef.current;
        if (node) {
            const callback = (e: any) => {
                const progress = e.target?.currentTime / e.target?.duration;
                setProgress((progress * 100).toFixed(2));
            };
            node.addEventListener('timeupdate', callback);
            return () => node.removeEventListener('timeupdate', callback);
        }
    }, [videoRef]);

    return (
        <div className="flex bg-black/30 p-1 leading-0">
            <input
                min={0}
                max={100}
                type="range"
                step={0.1}
                value={progress}
                onChange={onChange}
                className="progress h-1 w-full accent-primary"
            />
        </div>
    );
}

const Video = forwardRef<HTMLVideoElement | null, VideoProps>(
    ({ className, src, controls, ...props }, ref) => {
        const [playing, setPlaying] = useState(false);
        const videoRef = useRef<HTMLVideoElement>(null);

        useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement);

        useEffect(() => {
            const node = videoRef.current;
            if (node) {
                node.addEventListener('play', () => setPlaying(true));
                node.addEventListener('pause', () => setPlaying(false));
                return () => {
                    node.removeEventListener('play', () => setPlaying(true));
                    node.removeEventListener('pause', () => setPlaying(false));
                };
            }
        }, []);

        const onClick = () => {
            if (!videoRef.current) return;
            if (videoRef.current.paused) videoRef.current.play();
            else videoRef.current.pause();
        };

        return (
            <figure
                className="group relative my-auto w-full pt-full"
                onClick={onClick}
                data-playstate={playing ? 'playing' : 'paused'}>
                <video
                    ref={videoRef}
                    controls={false}
                    muted
                    loop
                    autoPlay={false}
                    playsInline
                    className={cn(
                        'absolute left-0 top-1/2 my-auto max-h-full w-full -translate-y-1/2 cursor-pointer object-contain',
                        className,
                    )}
                    {...props}>
                    <source src={src} type="video/mp4" />
                </video>
                <div
                    className={cn(
                        'absolute cursor-pointer p-2 opacity-0 transition-opacity group-hover:opacity-100 group-data-[playstate=paused]:opacity-100',
                        controls
                            ? 'bottom-0 grid w-full grid-cols-[auto,1fr] items-center gap-2 text-xl'
                            : ' left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl',
                    )}
                    id="video-controls">
                    <button
                        className="relative bg-black/30 p-1 text-white"
                        type="button"
                        data-state={playing ? 'play' : 'pause'}>
                        {playing ? (
                            <PauseIcon className="h-em" />
                        ) : (
                            <PlayIcon className="h-em" />
                        )}
                    </button>
                    {controls && <Progress videoRef={videoRef} />}
                </div>
            </figure>
        );
    },
);

Video.displayName = 'Video';

export default Video;
